import React, { FC, useCallback, useMemo, useState } from 'react';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import { Carousel } from '../../pages/aiStudio/components/presets/carousel';
import { DISCOVERY_PRINTING_PAYLOADS } from './discoveryPrintMethods';
import { ZTooltip } from '../ztooltip';

// ─── Data ────────────────────────────────────────────────────────────────────

const PREDEFINED_PRINTING_METHODS: DiscoveryItem[] = [
    {
        id: 'dtg-printing',
        name: 'DTG Printing',
        description: 'Direct-to-garment printing for high-detail, full-color designs directly onto fabric. Perfect for photo-realistic apparel.'
    },
    {
        id: 'wood-engraving',
        name: 'Wood Engraving',
        description: 'Applies a warm wood-burned effect. Customers add text and graphics that look etched into the wood surface.'
    },
    {
        id: 'pixelation',
        name: 'Pixelation',
        description: 'Applies a pixelated, mosaic-style effect to designs for a bold, retro low-res look.'
    },
    {
        id: 'laser-engraving',
        name: 'Laser Engraving',
        description: 'Limits customization to single-color engraving. Customers can add text and simple graphics with a precise, premium feel.'
    },
    {
        id: 'screenprinting',
        name: 'Screenprinting',
        description: 'Restricts the palette to a fixed set of spot colors. Ideal for bold, vibrant designs with limited color choices.'
    },
    {
        id: 'monochromie',
        name: 'Monochromie',
        description: 'Single-color printing only. Customers design with one ink color, creating a clean and minimal customization experience.'
    },
    {
        id: 'pantone-colors',
        name: 'Pantone Colors',
        description: 'Restricts color selection to Pantone swatches. Ensures color accuracy and consistency across different print runs.'
    },
    {
        id: 'cmyk-colors',
        name: 'CMYK Colors',
        description: 'Full-color process printing. Customers get the widest color range for photographic and complex multicolor designs.'
    },
    {
        id: 'uv-printing',
        name: 'UV Printing',
        description: 'UV-cured ink printing on rigid surfaces like wood, metal, and plastic. Supports full-color with raised texture effects.'
    },
    {
        id: 'heat-transfer',
        name: 'Heat Transfer',
        description: 'Heat-pressed vinyl transfers for simple, durable designs. Best for text, numbers, and basic shapes.'
    }
];

const PREDEFINED_PRICING_RULES: DiscoveryItem[] = [
    {
        id: 'per-customization',
        name: 'Price by element count',
        description: 'Flat customization fee that scales with how many elements the design has (1–3 elements $5, 4+ $10).'
    },
    {
        id: 'per-print-area',
        name: 'Price per design side',
        description: 'Fee based on how many sides are customized (1 side $5, 2+ sides $10). Front only costs less than front + back.'
    },
    {
        id: 'per-color',
        name: 'Color surcharge',
        description: 'Surcharge by number of ink colors used (first 2 colors free, 3+ adds $5). Ideal for screenprinting and spot-color workflows.'
    },
    {
        id: 'quantity-tiers',
        name: 'Quantity discount',
        description: 'Volume discount: the per-element customization price drops as the order quantity grows (1+ $10, 10+ $8, 50+ $5).'
    },
    {
        id: 'price-per-text-element',
        name: 'Price per text element',
        description: 'Charge a fixed fee for each text element the customer adds ($5 per text).'
    },
    {
        id: 'price-per-image-upload',
        name: 'Price per image upload',
        description: 'Charge a fixed fee for each image element added to the design ($10 per image).'
    },
    {
        id: 'price-by-element-size',
        name: 'Price by element size',
        description: 'Price each element by its printed area (≤ 50 cm² $5, larger $10). Bigger elements cost more.'
    },
    {
        id: 'price-by-canvas-size',
        name: 'Price by canvas size',
        description: 'Incremental fee based on the total print-area (canvas) size — $2 per 10 cm² of available canvas.'
    },
    {
        id: 'price-by-design-size',
        name: 'Price by design size',
        description: 'Price by the total customized area across the design (≤ 50 cm² $3, larger $8).'
    },
    {
        id: 'setup-fee',
        name: 'Setup fee',
        description: 'One-time setup fee applied to the order whenever the product is customized ($15 flat).'
    }
];

const PREDEFINED_AI_TOOLS: DiscoveryItem[] = [
    { id: 'ai-image-generation', name: 'Image Generation', description: 'Let customers generate and edit images with AI directly inside the customizer.' },
    {
        id: 'ai-design-generation',
        name: 'Design Generation',
        description: 'AI-powered design suggestions that customers can apply to their products in one click.'
    },
    {
        id: 'ai-product-staging',
        name: 'Product Staging',
        description: 'Automatically generate lifestyle scenes and backgrounds for product previews using AI.'
    },
    {
        id: 'ai-virtual-try-on',
        name: 'Virtual Try-On',
        description: 'Let customers see how the customized product looks on them using AI-powered virtual try-on.'
    }
];

const PREDEFINED_TOOLS: DiscoveryItem[] = [
    {
        id: 'get-a-quote',
        name: 'Get a Quote',
        description: 'Replaces Add to Cart with a quote request form. Customers fill in their details and submit a quotation request.'
    }
    // Temporarily hidden (2026-07-06): AI Vectorizer + AI Background Remover pills. May be restored here or
    // moved to another category. Keep their payloads in discoveryTools.ts in sync when restoring.
    // {
    //     id: 'ai-vectorizer',
    //     name: 'AI Vectorizer',
    //     description: 'Convert customer-uploaded images into clean, scalable vector artwork with AI — perfect for crisp, print-ready designs at any size.'
    // },
    // {
    //     id: 'ai-background-remover',
    //     name: 'AI Background Remover',
    //     description: 'Automatically remove the background from uploaded images with AI, leaving a clean cutout ready to customize.'
    // }
];

type CategoryId = 'printing' | 'pricing' | 'ai' | 'tools';

interface CategoryDef {
    id: CategoryId;
    label: string;
    items: DiscoveryItem[];
    color: string;
    saveLabel: string;
    buildYourOwnTooltip?: string;
    hasBuildYourOwn: boolean;
}

// ─── Styled ──────────────────────────────────────────────────────────────────

const PanelContainer = styled.div`
    flex-shrink: 0;
    padding: 10px 24px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const TabsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const CategoryTab = styled.button<{ $active: boolean; $color: string }>`
    padding: 5px 14px;
    border: 1.5px solid ${(props) => (props.$active ? props.$color : 'transparent')};
    border-radius: 6px;
    background: ${(props) => (props.$active ? props.$color + '12' : 'transparent')};
    color: ${(props) => (props.$active ? props.$color : '#64748b')};
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;

    &:hover {
        color: ${(props) => props.$color};
        background: ${(props) => props.$color + '08'};
    }
`;

const ContentRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const CarouselWrapper = styled.div`
    flex: 1;
    min-width: 0;
`;

const PillButton = styled.button<{ $selected: boolean; $color: string }>`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding: 6px 16px;
    border: 1.5px solid ${(props) => (props.$selected ? props.$color : '#d1d5db')};
    border-radius: 20px;
    background: ${(props) => (props.$selected ? props.$color + '14' : 'white')};
    color: ${(props) => (props.$selected ? props.$color : '#374151')};
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
        border-color: ${(props) => props.$color};
        background: ${(props) => props.$color + '0a'};
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.45;
        border-color: #d1d5db;
        background: white;
        color: #374151;
    }
`;

const StickyEnd = styled.div`
    position: sticky;
    right: 0;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding-left: 16px;
    background: linear-gradient(to right, transparent 0%, white 12px);
    z-index: 2;
`;

const BuildYourOwnItem = styled.button`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 16px;
    border: 1.5px dashed #9ca3af;
    border-radius: 20px;
    background: white;
    color: #6b7280;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
        border-color: #6b7280;
        color: #374151;
    }
`;

const SaveActionsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
`;

const SaveLink = styled.button<{ $color?: string }>`
    padding: 0;
    border: none;
    background: none;
    color: ${(props) => props.$color ?? '#2196f3'};
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    text-decoration: underline;
    transition: color 0.2s ease;

    &:hover {
        opacity: 0.8;
    }
`;

const QuantityRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 500;
    color: #64748b;
`;

const QuantityPill = styled.button<{ $selected: boolean; $color: string }>`
    flex-shrink: 0;
    padding: 4px 14px;
    border: 1.5px solid ${(props) => (props.$selected ? props.$color : '#d1d5db')};
    border-radius: 16px;
    background: ${(props) => (props.$selected ? props.$color + '14' : 'white')};
    color: ${(props) => (props.$selected ? props.$color : '#374151')};
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: ${(props) => props.$color};
        background: ${(props) => props.$color + '0a'};
    }
`;

// ─── Types & Props ───────────────────────────────────────────────────────────

export interface DiscoveryItem {
    id: string;
    name: string;
    description?: string;
    // Force-disable the pill regardless of category wiring (e.g. tools whose function isn't hooked up yet).
    disabled?: boolean;
}

// Keep backward compat alias
export type PrintTypeItem = DiscoveryItem;

interface DiscoveryModePanelProps {
    selections: Record<string, string | null>;
    onSelect: (category: string, id: string | null) => void;
    onSave: (category: string, item: DiscoveryItem) => void;
    onBuildYourOwn: (category: string) => void;
    // Simulated order quantity, shown only for the quantity-tiers pricing rule (which prices by order
    // quantity). The sandbox has no footer quantity selector, so this drives it.
    quantity?: number;
    quantityPresets?: number[];
    onQuantityChange?: (qty: number) => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const DiscoveryModePanel: FC<DiscoveryModePanelProps> = ({
    selections,
    onSelect,
    onSave,
    onBuildYourOwn,
    quantity,
    quantityPresets,
    onQuantityChange
}) => {
    const CATEGORIES: CategoryDef[] = useMemo(
        () => [
            {
                id: 'printing',
                label: T._('Printing Methods', 'Admin'),
                items: PREDEFINED_PRINTING_METHODS,
                color: '#2196f3',
                saveLabel: T._('Save "{0}" as my Printing Method', 'Admin'),
                hasBuildYourOwn: true,
                buildYourOwnTooltip: T._('Create a custom printing method from scratch', 'Admin')
            },
            {
                id: 'pricing',
                label: T._('Pricing Rules', 'Admin'),
                items: PREDEFINED_PRICING_RULES,
                color: '#8b5cf6',
                saveLabel: T._('Save "{0}" as my Pricing Rule', 'Admin'),
                hasBuildYourOwn: true,
                buildYourOwnTooltip: T._('Create a custom pricing rule from scratch', 'Admin')
            },
            {
                id: 'ai',
                label: T._('AI Creative Tools', 'Admin'),
                items: PREDEFINED_AI_TOOLS,
                color: '#ec4899',
                saveLabel: T._('Activate "{0}"', 'Admin'),
                hasBuildYourOwn: true,
                buildYourOwnTooltip: T._('Explore all AI Creative Tools', 'Admin')
            },
            {
                id: 'tools',
                label: T._('Tools', 'Admin'),
                items: PREDEFINED_TOOLS,
                color: '#f59e0b',
                saveLabel: T._('Activate "{0}"', 'Admin'),
                hasBuildYourOwn: false
            }
        ],
        []
    );

    const [activeTab, setActiveTab] = useState<CategoryId>('printing');
    const category = CATEGORIES.find((c) => c.id === activeTab)!;
    const selectedId = selections[activeTab] ?? null;
    const selectedItem = category.items.find((item) => item.id === selectedId) ?? null;

    const handleItemClick = useCallback(
        (item: DiscoveryItem) => {
            onSelect(activeTab, selectedId === item.id ? null : item.id);
        },
        [activeTab, selectedId, onSelect]
    );

    return (
        <PanelContainer>
            {/* Category tabs */}
            <TabsRow>
                {CATEGORIES.map((cat) => (
                    <CategoryTab key={cat.id} $active={activeTab === cat.id} $color={cat.color} onClick={() => setActiveTab(cat.id)}>
                        {cat.label}
                        {selections[cat.id] ? ' \u2022' : ''}
                    </CategoryTab>
                ))}
            </TabsRow>

            {/* Items carousel for active tab */}
            <ContentRow>
                <CarouselWrapper>
                    <Carousel variant='default' gap={8} scrollAmount={200}>
                        {category.items.map((item) => {
                            // Printing methods are only interactive once they have a payload wired in
                            // DISCOVERY_PRINTING_PAYLOADS; the rest are placeholders, so disable them.
                            // Items may also opt out explicitly via `disabled` (e.g. tools not yet hooked up).
                            const isWired = activeTab !== 'printing' || item.id in DISCOVERY_PRINTING_PAYLOADS;
                            return (
                                <ZTooltip key={item.id} position='bottom' title={item.description ?? ''} enterDelay={300} exitDelay={0} interactive={false}>
                                    <PillButton
                                        $selected={selectedId === item.id}
                                        $color={category.color}
                                        disabled={!isWired || Boolean(item.disabled)}
                                        onClick={() => handleItemClick(item)}
                                    >
                                        {item.name}
                                    </PillButton>
                                </ZTooltip>
                            );
                        })}
                        {category.hasBuildYourOwn && (
                            <StickyEnd>
                                <ZTooltip position='bottom' title={category.buildYourOwnTooltip ?? ''} enterDelay={300} exitDelay={0} interactive={false}>
                                    <BuildYourOwnItem onClick={() => onBuildYourOwn(activeTab)}>{'+ ' + T._('Build your own', 'Admin')}</BuildYourOwnItem>
                                </ZTooltip>
                            </StickyEnd>
                        )}
                    </Carousel>
                </CarouselWrapper>
            </ContentRow>
            {/* Simulated order quantity — only for the quantity-tiers rule, which prices by order quantity */}
            {activeTab === 'pricing' && selectedId === 'quantity-tiers' && onQuantityChange && (
                <QuantityRow>
                    <span>{T._('Simulated quantity', 'Admin')}:</span>
                    {(quantityPresets ?? [1, 10, 50, 100]).map((preset) => (
                        <QuantityPill key={preset} $selected={quantity === preset} $color={category.color} onClick={() => onQuantityChange(preset)}>
                            {preset}
                        </QuantityPill>
                    ))}
                </QuantityRow>
            )}
            {/* Save links for all active selections across categories */}
            {CATEGORIES.some((cat) => selections[cat.id]) && (
                <SaveActionsRow>
                    {CATEGORIES.map((cat) => {
                        const selId = selections[cat.id];
                        if (!selId) return null;
                        const item = cat.items.find((i) => i.id === selId);
                        if (!item) return null;
                        return (
                            <SaveLink key={cat.id} $color={cat.color} onClick={() => onSave(cat.id, item)}>
                                {cat.saveLabel.replace('{0}', item.name)}
                            </SaveLink>
                        );
                    })}
                </SaveActionsRow>
            )}
        </PanelContainer>
    );
};

export default DiscoveryModePanel;
